import { zodResolver } from "@hookform/resolvers/zod";
import {
  Menu,
  MenuContent,
  MenuLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuTrigger,
} from "@rosc/ui/v32/menu";
import { t } from "i18next";
import { ChevronDown } from "lucide-react";
import {
  forwardRef,
  type JSX,
  type ReactNode,
  useEffect,
  useImperativeHandle,
} from "react";
import { type FieldErrors, useForm } from "react-hook-form";
import { AppConfig } from "@/common/appConfig";
import { DefaultRecordingTemplates } from "@/common/enums/enums";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { useAccountDefaultTemplate } from "@/lib/account-default-template";
import {
  readFavorites,
  useTemplatePreferenceVersion,
} from "@/lib/template-preferences";
import { useTemplateList } from "@/lib/template-queries";
import { cn } from "@/lib/utils";
import {
  RecorderFormSchema,
  type RecorderFormSchemaType,
} from "@/schemas/newVisit.schema";
import { useAuthStore } from "@/store/auth.store";
import { FormField } from "../form/FormField";
import { V3Button } from "../v3/button";

interface PickerGroup {
  label?: string;
  options: { value: string; label: string }[];
}

const PICKER_MENU = cn(
  "max-h-[min(60dvh,420px)] w-[min(320px,calc(100vw-32px))] overflow-y-auto",
);

/**
 * v3.2: one row of the note settings card (label on the left, the value and
 * a chevron on the right) that opens the same v3 menu as the recorder pills.
 */
const PickerRow = ({
  label,
  value,
  groups,
  disabled,
  onChange,
  testId,
}: {
  label: string;
  value: string;
  groups: PickerGroup[];
  disabled?: boolean;
  onChange: (value: string) => void;
  testId?: string;
}): JSX.Element => {
  const current = groups
    .flatMap((group) => group.options)
    .find((option) => option.value === value);
  return (
    <Menu>
      <MenuTrigger asChild>
        <button
          aria-label={`${label}: ${current?.label ?? value}`}
          className="flex min-h-12 w-full min-w-0 items-center gap-3 px-4 text-left font-v3 transition-colors hover:bg-v3-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-v3-brand-ring disabled:opacity-60 data-[state=open]:bg-v3-surface-2"
          data-testid={testId}
          disabled={disabled}
          type="button"
        >
          <span className="shrink-0 text-[14px] text-v3-text-2">{label}</span>
          <span className="ml-auto min-w-0 truncate text-[14px] font-semibold text-v3-text">
            {current?.label ?? value}
          </span>
          <ChevronDown aria-hidden className="size-4 shrink-0 text-v3-text-3" />
        </button>
      </MenuTrigger>
      <MenuContent align="end" className={PICKER_MENU}>
        <MenuRadioGroup value={value} onValueChange={onChange}>
          {groups.map((group, index) =>
            group.options.length ? (
              // biome-ignore lint/suspicious/noArrayIndexKey: groups are fixed per picker.
              <div key={index}>
                {group.label && <MenuLabel>{group.label}</MenuLabel>}
                {group.options.map((option) => (
                  <MenuRadioItem key={option.value} value={option.value}>
                    <span className="min-w-0 truncate">{option.label}</span>
                  </MenuRadioItem>
                ))}
              </div>
            ) : null,
          )}
        </MenuRadioGroup>
      </MenuContent>
    </Menu>
  );
};

const PickerCard = ({ children }: { children: ReactNode }): JSX.Element => (
  <div className="overflow-hidden rounded-v3-panel bg-v3-panel shadow-v3-hairline-strong [&>*+*]:border-t [&>*+*]:border-v3-border">
    {children}
  </div>
);

export interface RecorderFormHandle {
  submitForm: () => Promise<void>;
}

export interface RecorderFormProps {
  onChange?: () => void;
  onTitleChange?: (title: string) => void;
  titleOnly?: boolean;
  showTitle?: boolean;
  onSubmitData?: (data: RecorderFormSchemaType) => Promise<void>;
  /**
   * The title typed elsewhere (the v3.2 New visit heading): kept in the form
   * so a later fallback to this form submits it.
   */
  syncedTitle?: string;
  /** Values kept with a recording on this device (offline recording). */
  initialValues?: Partial<RecorderFormSchemaType>;
  /**
   * Template requested by the EMR launch link: a default template name or a
   * custom template id. Applied once it is known to exist; otherwise the
   * usual default stays selected.
   */
  preferredTemplate?: string;
}

const RecorderForm = forwardRef<RecorderFormHandle, RecorderFormProps>(
  (
    {
      onSubmitData,
      onChange,
      onTitleChange,
      titleOnly,
      showTitle,
      initialValues,
      preferredTemplate,
      syncedTitle,
    },
    ref,
  ) => {
    // Read at render time (stories and tests switch the flag).
    const v3 = AppConfig.enableAppRedesign === true;
    const { getUser, user, userData } = useAuthStore.getState();
    const {
      templates,
      loading: templatesLoading,
      error: templatesError,
      refresh,
    } = useTemplateList();

    useEffect(() => {
      if (user?.email && !userData) {
        void getUser();
      }
    }, []);

    // Same account default as Settings and Templates.
    useTemplatePreferenceVersion();
    const account = userData?.public_id;
    const { value: defaultTemplateValue, loading: defaultTemplateLoading } =
      useAccountDefaultTemplate();
    const favorites = readFavorites(account);

    const formMethods = useForm<RecorderFormSchemaType>({
      resolver: zodResolver(RecorderFormSchema),
      defaultValues: {
        title: "",
        template: defaultTemplateValue,
        style: userData?.style || "Bullet Point",
        format: userData?.format || "Concise",
        examination: "Physical",
        templateName: defaultTemplateValue,
        customTemplatePublicId: null,
        ...initialValues,
      },
    });

    useEffect(() => {
      if (syncedTitle === undefined) return;
      if (formMethods.getValues("title") !== syncedTitle)
        formMethods.setValue("title", syncedTitle);
    }, [syncedTitle, formMethods]);

    useEffect(() => {
      const subscription = formMethods.watch(() => onChange?.());
      return () => subscription.unsubscribe();
    }, [formMethods, onChange]);

    const defaultTemplates: string[] = Object.values(DefaultRecordingTemplates);
    const styleOptions: string[] = ["Paragraph", "Bullet Point"];
    const formatOptions: string[] = ["Concise", "Detailed"];
    const examinationOptions: string[] = ["Physical", "Virtual"];
    // Stored values stay in English; only the labels follow the interface.
    const optionLabels: Record<string, string> = {
      Paragraph: t("noteOptions.style.paragraph"),
      "Bullet Point": t("noteOptions.style.bulletPoint"),
      Concise: t("noteOptions.format.concise"),
      Detailed: t("noteOptions.format.detailed"),
      Physical: t("noteOptions.exam.physical"),
      Virtual: t("noteOptions.exam.virtual"),
    };
    const selectedTemplate = formMethods.watch("template");
    const isCustomTemplate = !defaultTemplates.includes(selectedTemplate);
    const isVoiceToTextTemplate =
      selectedTemplate === DefaultRecordingTemplates.VOICE_TO_TEXT.toString();

    useEffect(() => {
      const currentTemplate = formMethods.getValues("template");
      if (
        !initialValues?.template &&
        !formMethods.getFieldState("template").isDirty &&
        currentTemplate !== defaultTemplateValue
      ) {
        formMethods.setValue("template", defaultTemplateValue);
      }
    }, [defaultTemplateValue, formMethods, initialValues?.template]);

    useEffect(() => {
      if (!preferredTemplate || initialValues?.template) return;
      if (formMethods.getFieldState("template").isDirty) return;
      const available =
        defaultTemplates.includes(preferredTemplate) ||
        templates.some((template) => template.public_id === preferredTemplate);
      if (available && formMethods.getValues("template") !== preferredTemplate)
        formMethods.setValue("template", preferredTemplate);
    }, [
      preferredTemplate,
      templates,
      defaultTemplateValue,
      formMethods,
      initialValues?.template,
    ]);

    const customTemplates: { public_id: string; name: string }[] =
      templates?.map((tpl) => ({
        public_id: tpl.public_id,
        name: tpl.name,
      })) || [];
    // Starred templates first, each listed once.
    const favoriteOptions = [
      ...defaultTemplates.map((name) => ({ value: name, name })),
      ...customTemplates.map((tpl) => ({
        value: tpl.public_id,
        name: tpl.name,
      })),
    ].filter((option) => favorites.includes(option.value));
    const builtInOptions = defaultTemplates.filter(
      (name) => !favorites.includes(name),
    );
    const otherCustomTemplates = customTemplates.filter(
      (tpl) => !favorites.includes(tpl.public_id),
    );

    const showValidationErrors = (
      errors: FieldErrors<RecorderFormSchemaType>,
    ): void => {
      const firstError = Object.values(errors)[0];
      if (firstError?.message) {
        toast({
          variant: "error",
          title: t("visits.sidebar.messages.requestFailed.title"),
          description: String(firstError.message),
        });
      }
    };

    async function onSubmit(
      formData: RecorderFormSchemaType,
    ): Promise<RecorderFormSchemaType> {
      if (templatesLoading || defaultTemplateLoading) return formData;
      if (onSubmitData) {
        const template = formData.template;
        const selectedCustom = customTemplates.find(
          (tpl) => tpl.public_id === template,
        );
        if (isVoiceToTextTemplate || isCustomTemplate) {
          formData.style = "";
          formData.format = "";
          formData.examination = "";
        }
        if (selectedCustom) {
          formData.templateName = "Custom";
          formData.customTemplatePublicId = selectedCustom.public_id;
        } else {
          formData.templateName = template;
          formData.customTemplatePublicId = null;
        }
        await onSubmitData(formData);
      }
      return formData;
    }

    useImperativeHandle(ref, () => ({
      submitForm: async (): Promise<void> => {
        await formMethods.handleSubmit(onSubmit, showValidationErrors)();
      },
    }));

    if (v3) {
      const templateGroups: PickerGroup[] = [
        {
          label: t("templates.favorites.group"),
          options: favoriteOptions.map((option) => ({
            value: option.value,
            label: option.name,
          })),
        },
        {
          label: t("components.recorderForm.defaultTemplatesSelectLabel"),
          options: builtInOptions.map((name) => ({ value: name, label: name })),
        },
        {
          label: t("components.recorderForm.customTemplatesSelectLabel"),
          options: otherCustomTemplates.map((tpl) => ({
            value: tpl.public_id,
            label: tpl.name,
          })),
        },
      ];
      const choices = (values: string[]): PickerGroup[] => [
        {
          options: values.map((value) => ({
            value,
            label: optionLabels[value] ?? value,
          })),
        },
      ];
      return (
        <Form {...formMethods}>
          <form
            className="flex w-full flex-col gap-4 font-v3"
            data-testid="recorder-form-v3"
            onSubmit={formMethods.handleSubmit(onSubmit, showValidationErrors)}
          >
            {templatesError && (
              <div
                className="flex items-center gap-3 rounded-xl bg-v3-rec-tint px-4 py-3"
                role="alert"
              >
                <p className="flex-1 text-[14px] leading-5 text-v3-rec-text">
                  {t("error.unknown")}
                </p>
                <V3Button
                  variant="secondary"
                  onClick={() => {
                    void refresh();
                  }}
                >
                  {t("common.retry", "Retry")}
                </V3Button>
              </div>
            )}
            {showTitle && (
              <FormField
                control={formMethods.control}
                name="title"
                render={({ field }) => (
                  <FormItem className="w-full space-y-1.5">
                    <FormLabel className="text-[13px] font-medium text-v3-text-2">
                      {t("visits.sidebar.title.label")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        className="h-11"
                        maxLength={128}
                        placeholder={t("visits.sidebar.title.placeholder")}
                        onChange={(event) => {
                          field.onChange(event);
                          onTitleChange?.(event.target.value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            {!titleOnly && (
              <PickerCard>
                <PickerRow
                  disabled={templatesLoading || defaultTemplateLoading}
                  groups={templateGroups}
                  label={t("components.recorderForm.templateLabel")}
                  testId="recorder-form-template"
                  value={selectedTemplate}
                  onChange={(value) =>
                    formMethods.setValue("template", value, {
                      shouldDirty: true,
                    })
                  }
                />
                {!isVoiceToTextTemplate && !isCustomTemplate && (
                  <>
                    <PickerRow
                      groups={choices(styleOptions)}
                      label={t("components.recorderForm.styleLabel")}
                      testId="recorder-form-style"
                      value={formMethods.watch("style") ?? ""}
                      onChange={(value) =>
                        formMethods.setValue("style", value, {
                          shouldDirty: true,
                        })
                      }
                    />
                    <PickerRow
                      groups={choices(formatOptions)}
                      label={t("components.recorderForm.formatLabel")}
                      testId="recorder-form-format"
                      value={formMethods.watch("format") ?? ""}
                      onChange={(value) =>
                        formMethods.setValue("format", value, {
                          shouldDirty: true,
                        })
                      }
                    />
                    <PickerRow
                      groups={choices(examinationOptions)}
                      label={t("components.recorderForm.examinationLabel")}
                      testId="recorder-form-examination"
                      value={formMethods.watch("examination") ?? ""}
                      onChange={(value) =>
                        formMethods.setValue("examination", value, {
                          shouldDirty: true,
                        })
                      }
                    />
                  </>
                )}
              </PickerCard>
            )}
          </form>
        </Form>
      );
    }

    return (
      <Form {...formMethods}>
        <form
          className="flex flex-col gap-6 w-full"
          onSubmit={formMethods.handleSubmit(onSubmit, showValidationErrors)}
        >
          {templatesError && (
            <div role="alert">
              <p>{t("error.unknown")}</p>
              <Button
                type="button"
                onClick={() => {
                  void refresh();
                }}
              >
                {t("common.retry", "Retry")}
              </Button>
            </div>
          )}
          <div className="flex flex-col gap-4">
            {!titleOnly && (
              <FormField
                control={formMethods.control}
                name="template"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>
                      {t("components.recorderForm.templateLabel")}
                    </FormLabel>
                    <FormControl>
                      <Select
                        disabled={templatesLoading || defaultTemplateLoading}
                        value={field.value}
                        onValueChange={(selectedValue: string) => {
                          field.onChange(selectedValue);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t(
                              "components.recorderForm.templatePlaceholder",
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {favoriteOptions.length > 0 && (
                            <SelectGroup>
                              <SelectLabel>
                                {t("templates.favorites.group")}
                              </SelectLabel>
                              {favoriteOptions.map((option) => (
                                <SelectItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.name}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          )}
                          {builtInOptions.length > 0 && (
                            <SelectGroup>
                              <SelectLabel>
                                {t(
                                  "components.recorderForm.defaultTemplatesSelectLabel",
                                )}
                              </SelectLabel>
                              {builtInOptions.map((option) => (
                                <SelectItem key={option} value={option}>
                                  {option}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          )}
                          {otherCustomTemplates.length > 0 && (
                            <SelectGroup>
                              <SelectLabel>
                                {t(
                                  "components.recorderForm.customTemplatesSelectLabel",
                                )}
                              </SelectLabel>
                              {otherCustomTemplates.map((customTpl) => (
                                <SelectItem
                                  key={customTpl.public_id}
                                  value={customTpl.public_id}
                                >
                                  {customTpl.name}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          )}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            {showTitle && (
              <FormField
                control={formMethods.control}
                name="title"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>{t("visits.sidebar.title.label")}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        maxLength={128}
                        placeholder={t("visits.sidebar.title.placeholder")}
                        onChange={(event) => {
                          field.onChange(event);
                          onTitleChange?.(event.target.value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            {/* Voice to text template and custom templates doesn't support any more options */}
            {!titleOnly && !isVoiceToTextTemplate && !isCustomTemplate && (
              <>
                <FormField
                  control={formMethods.control}
                  name="style"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>
                        {t("components.recorderForm.styleLabel")}
                      </FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={t("noteOptions.selectStyle")}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {styleOptions.map((option) => (
                              <SelectItem key={option} value={option}>
                                {optionLabels[option] ?? option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={formMethods.control}
                  name="format"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>
                        {t("components.recorderForm.formatLabel")}
                      </FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={t("noteOptions.selectFormat")}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {formatOptions.map((option) => (
                              <SelectItem key={option} value={option}>
                                {optionLabels[option] ?? option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={formMethods.control}
                  name="examination"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>
                        {t("components.recorderForm.examinationLabel")}
                      </FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={t("noteOptions.selectExamination")}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {examinationOptions.map((option) => (
                              <SelectItem key={option} value={option}>
                                {optionLabels[option] ?? option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}
          </div>
        </form>
      </Form>
    );
  },
);

RecorderForm.displayName = "RecorderForm";
export default RecorderForm;
